import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconButton, Select, toast } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { GET_COMPANIES } from "graphql/company";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import { accountHolderPath } from "utils/platformAccounts/accountPaths";

interface CompanyChoice {
  id: string;
  businessLegalName: string;
  businessDBA?: string;
}

export function CompanySwitcher({
  companyId,
  name,
}: {
  companyId: string;
  name: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const companies = useCompanyConnection<CompanyChoice>({
    query: GET_COMPANIES,
    variables: { searchTerm },
    field: "getCompanies",
  });
  const options = new Map(
    companies.rows.map((company) => [
      company.id,
      {
        id: company.id,
        label: company.businessDBA || company.businessLegalName,
      },
    ]),
  );
  if (!options.has(companyId))
    options.set(companyId, { id: companyId, label: name });
  return (
    <Inline gap="var(--space-3)" wrap={false}>
      <Select
        aria-label={t("Account")}
        size="md"
        width={240}
        value={companyId}
        clearable={false}
        options={[...options.values()]}
        loading={companies.loading || companies.isFetchingMore}
        onInputChange={search.onChange}
        onScrollEnd={companies.loadMore}
        onChange={(id) => {
          if (!id || id === companyId) return;
          const next = new URLSearchParams();
          const current = new URLSearchParams(location.search);
          ["tab", "from", "to"].forEach((key) => {
            const value = current.get(key);
            if (value) next.set(key, value);
          });
          navigate(`${accountHolderPath(id)}?${next}`);
        }}
      />
      <IconButton
        variant="outlined"
        aria-label={t("Copy account link")}
        onClick={() => {
          void navigator.clipboard
            .writeText(
              `${window.location.origin}${location.pathname}${location.search}`,
            )
            .then(() => toast.success(t("Account link copied")))
            .catch(() => toast.error(t("Could not copy account link")));
        }}
      >
        <HugeiconsIcon icon={Link01Icon} size={18} />
      </IconButton>
    </Inline>
  );
}
